import React, { useEffect, useState, useRef } from "react";
import { IActionHandler, MessageDisplayType } from "@preveil-api";
import {
  Account, useAppSelector, DriveUrlParams, useAppDispatch, useFetchWatched, GlobalErrorMessages, DriveErrorMessages, Message, MessageAnchors, isNumber,
  MessageHandlerDisplayType, GenericListEntity, Pagination, GenericEntryEntity, DriveLimits, useWatchNode, DriveWatchedErrorMessages, DriveWatchedMessages, DriveEntryType, WatchedTabs,
  useGetPath, WatchedTabTitles, PathInfo, DriveConstants, DefaultRoutes
} from "src/common";
import { Loading } from "src/components";
import { EmptyMessage } from ".";
import { Toolbar, ListView, Breadcrumbs } from "./watch-list";
import { uiActions, driveActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
    current_account: Account;
    router_params: DriveUrlParams;
}

function WatchedManagerComponent(props: AllProps) {
    const { current_account, router_params } = props; //  => Will be needed for fetching descendants  router_params,
    const { collection_id, id } = router_params;
    const dispatch = useAppDispatch();
    const is_loading = useAppSelector((state: RootState) => state.drive.is_loading);
    const selected = useAppSelector((state: RootState) => state.drive.selected);
    const pagination = useAppSelector((state: RootState) => state.drive.pagination);
    const [page, setPage] = useState<string>(DriveLimits.DRIVE_PAGE_DEFAULT);
    const [filter, setFilter] = useState<string>();
    const [error_message, setErrorMessage] = useState<string | undefined>();
    const [current_entry, setCurrentEntry] = useState<GenericEntryEntity | undefined>(); // NOTE: Entry Loaded in ListView
    // Description: Maintainer nodes fetched grants
    const path_list_ref = useRef<PathInfo[]>([]);
    const { getPath, reset: resetPathInfo, full_path_list, watched_node_ids } = useGetPath(current_account, true);
    const { fetchWatchedList, resetWatchList, watched_list, error: watch_list_error } = useFetchWatched(current_account);
    const { setBulkUnWatchState, reset: resetSetUnwatch, clearAllWatchedNodes, success: bulk_unwatch_complete, bulk_action_errors } = useWatchNode(current_account);

    function setPathListRef(_path_list_ref: PathInfo[]) {
        path_list_ref.current = _path_list_ref;
    }

    // Description: Handles navigation into descendants
    useEffect(() => {
        DriveWatchRequests.handleRefresh(true);
    }, [collection_id, id]);

    // Description: Return a pseudo GenericEntryEntity for Breadcrumbs
    useEffect(() => {
        if (!!full_path_list && !!id && collection_id !== WatchedTabs.root) {
            setCurrentEntry({
                collection_id,
                id,
                type: DriveEntryType.DIR,
                parent_id: "", name: "", version: "", last_modification_date: ""
            });
        }
    }, [full_path_list]);

    // Description: Handles first load on Drive state and websocket ready
    useEffect(() => {
        if (!!watched_list) {
            DriveWatchRequests.handleWatchNodesSuccess(watched_list);
            dispatch(driveActions.setLoading(false));
        }

        if (!!watch_list_error) {
            setErrorMessage(DriveWatchedErrorMessages.watched_list_failed);
            dispatch(driveActions.setLoading(false));
            resetWatchList();
        }
    }, [watched_list, watch_list_error]);


    // Description: Handle success and errors from unwatching nodes
    useEffect(() => {
        if (!!bulk_unwatch_complete) {
            dispatch(driveActions.setLoading(false));
            !!bulk_action_errors.length ?
                DriveWatchRequests.handlePageErrorMessage({
                    message: DriveWatchedErrorMessages.error_unwatch_bulk_nodes.replace(MessageAnchors.item_names, bulk_action_errors.join(", ")),
                    display_type: MessageHandlerDisplayType.toastr
                }) : dispatch(uiActions.handleSetMessage(new Message(DriveWatchedMessages.success_unwatch_bulk_nodes)));
            resetSetUnwatch();
            DriveWatchRequests.handleRefresh();
            dispatch(driveActions.setLoading(false));
        }
    }, [bulk_unwatch_complete]);

    // -------------------------------------------------------------------------------
    // Description: Handle all children component actions and store it
    // -------------------------------------------------------------------------------
    const DriveWatchRequests = {
        // Description: Handle fetch watch page either Root or descendants based or router parameters AND Refresh
        handleRefresh(reset_page: boolean = false) {
            dispatch(driveActions.setLoading(true));
            reset_page && ResetPage();
            (collection_id !== WatchedTabs.root && !!id) && getPath(collection_id, id);
            fetchWatchedList(collection_id, id, Number(reset_page ? DriveLimits.DRIVE_PAGE_DEFAULT : page));
            !!selected.length && dispatch(driveActions.setSelected([]));
        },

        // Description: Handles select all from List View header
        handleSelect(params: { entry?: GenericEntryEntity, selected: boolean }) {
            let new_selected = selected.slice();
            const _id = !!params.entry ? params.entry.id : null;
            if (!!_id) { // Note: individual check
                if (params.selected) {
                    !_.find(new_selected, (_selected: string) => (_selected === _id)) &&
                        new_selected.push(_id);
                } else {
                    const index = _.findIndex(new_selected, (_selected: string) => (_selected === _id));
                    index >= 0 && (new_selected.splice(index, 1));
                }
            } else {
                new_selected = params.selected ? _.map(watched_list?.entries, (entry: GenericEntryEntity) => (entry.id)) : [];
            }
            dispatch(driveActions.setSelected(new_selected));
        },

        //  Description: Navigate to page passed
        handlePaging(new_page: string) {
            if (new_page !== page && isNumber(new_page)) {
                dispatch(driveActions.setLoading(true));
                fetchWatchedList(collection_id, id, Number(new_page));
                setPage(new_page);
            } else {
                DriveWatchRequests.handlePageErrorMessage({
                    message: GlobalErrorMessages.default, stack: {
                        error: DriveErrorMessages.error_paginating.replace(MessageAnchors.page_number, new_page)
                    }
                });
            }
        },

        // Description: filters items in list and updates pagination
        handleFilter: setFilter,

        handleClearAllWatched: clearAllWatchedNodes,

        //  Description: Handle unwatch from menu or toolbar
        handleUnwatch(ids: string[]) {
            if (watched_list?.entries) {
                const entries = _.compact(_.map(ids, (id: string) => _.find(watched_list.entries, (entry) => entry.id === id)));
                entries.length > 0 ? setBulkUnWatchState(entries) : DriveWatchRequests.handleRefresh();
            } else {
                DriveWatchRequests.handlePageErrorMessage({
                    message: GlobalErrorMessages.default, stack: {
                        error: DriveWatchedErrorMessages.error_setting_node_watch
                    }
                });
            }
        },

        //  Description: Run after fetching watch_list
        handleWatchNodesSuccess(_watched_list: GenericListEntity) {
            const total_page_rows = _watched_list.entries.length;
            const total_count = watched_list?.pagination_info.total_count;
            const total_rows = total_count || _watched_list.entries.length;
            if (total_page_rows > 0 && total_rows > 0) {
                const pagination_item = new Pagination(Pagination.getPageIndex(page), total_page_rows, total_rows, pagination.pageSize).pagination_item;
                !!pagination_item && dispatch(driveActions.setPagination(pagination_item));
            } else {
                dispatch(driveActions.resetPagination());
            }
        },

        // Description: Handle all page / components top level errors
        handlePageErrorMessage(params: { message: string, stack?: any, display_type?: MessageDisplayType }) {
            const display_type = !!params.display_type ? params.display_type : MessageHandlerDisplayType.logger;
            dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
        }
    };

    //  Description: Handle all actions from Children forms
    function handlePageActions(action_obj: IActionHandler) {
        const callback = `handle${action_obj.actionType}`;
        if ((DriveWatchRequests as any)[callback] instanceof Function) {
            (DriveWatchRequests as any)[callback](action_obj.params);
        } else {
            const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
            DriveWatchRequests.handlePageErrorMessage({ message, stack: action_obj });
        }
    }

    // Description: Reset state supporting client side pagination
    function ResetPage() {
        setCurrentEntry(undefined);
        setPage(DriveLimits.DRIVE_PAGE_DEFAULT);
        dispatch(driveActions.resetPagination());
        resetPathInfo();
    }

    //  Description: Render the mid section
    function RenderWatch() {
        return <div className="watched-wrapper">
            {
                (!!current_entry && !!full_path_list && !!watched_node_ids) ?
                    <Breadcrumbs
                        tooltip_id="wm-tooltip"
                        current_account={current_account}
                        entry={current_entry}
                        full_path_list={full_path_list}
                        watched_node_ids={watched_node_ids}
                        root_item={WatchedTabTitles.root}
                        root_url={DefaultRoutes.drive_watched_root}
                        drive_module={DriveConstants.WATCHED}
                        handleAction={(action_obj: IActionHandler) => setPathListRef(action_obj.params.path_info)} /> :
                    <label className="bc-title mb-3">{WatchedTabTitles.root}</label>
            }
            <Toolbar
                is_loading={is_loading}
                pagination={pagination}
                selected={selected}
                counts={watched_list?.entries.length}
                handleAction={handlePageActions} />
            {
                !!watched_list ?
                    <ListView
                        current_account={current_account}
                        is_loading={is_loading}
                        selected={selected}
                        filter={filter}
                        watched_list={watched_list}
                        pagination={pagination}
                        handleAction={handlePageActions} /> :
                    is_loading ? <Loading className="in-place mt-5" /> :
                        <EmptyMessage message={(!!error_message ? error_message : undefined)} />
            }
        </div>;
    }

    return RenderWatch();
}

export default React.memo(WatchedManagerComponent);